import { useState } from "react";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Modal from "/src/components/modal/Modal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Form from "/src/components/form/Form";
import Button from "/src/components/UI/Button/Button";
import knowledgeService from "../../services/KnowledgeService";
import "/src/features/ai/llm-rag/css/KnowledgeModal.css";
import FromConnections from "./components/FromConnections";
import FromDocuments from "./components/FromDocuments";
import FromWebsites from "./components/FromWebsites";
import chatService from "../../services/ChatService";

const KnowledgeModal = NiceModal.create(({ title, sub_title, brainId, onUploadComplete }) => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const toast = useToastDispatchContext();

    const [selectedTab, setSelectedTab] = useState("Documents");
    const [file, setFile] = useState(null);
    const [url, setUrl] = useState("");
    const [loadAnim, setLoadAnim] = useState(false);

    const tabs = ["Documents", "Connections", "Websites"];

    const uploadFile = async (bulkId, file, brainId, toast) => {
        const formData = new FormData();
        formData.append("file", file);

        const response = await knowledgeService.uploadKnowledge(bulkId, brainId, formData);

        if (Array.isArray(response) && response[0]?.message === "File processing has started.") {
        } else {
            let parsedMessage = "Unknown error";

            if (Array.isArray(response) && response[0]?.message) {
                parsedMessage = response[0].message;
            }

            if (parsedMessage.includes("already exists")) {
                toast.error("File already exists", `The file ${file.name} already exists.`);
            } else {
                toast.error("Upload Error", "Failed to upload file.");
            }

            throw new Error(parsedMessage);
        }
    };

    const crawlUrl = async (bulkId, url, brainId, toast) => {
        const crawlOptions = {
            url,
            depth: 1,
            js: false,
            max_pages: 100,
            max_time: 60,
        };

        try {
            await knowledgeService.crawlWebsite(bulkId, brainId, crawlOptions);
        } catch (error) {
            toast.error("Crawl Error", "Failed to crawl the URL.");
            throw error;
        }
    };

    const onSubmit = async () => {
        if (!file && !url) {
            toast.error("Upload Error", "Please provide a file or a URL.");
            return;
        }

        setLoadAnim(true);

        const response = await chatService.createChat();
        if (!response[0].chat_id) {
            throw new Error("Failed to create chat: Missing chat_id");
        }

        const bulkId = response[0].chat_id;
        let uploadSuccess = true;

        try {
            const tasks = [];

            if (file) {
                const fileUpload = uploadFile(bulkId, file, brainId, toast).catch((error) => {
                    uploadSuccess = false;
                    return Promise.reject(error);
                });
                tasks.push(fileUpload);
            }

            if (url) {
                const urlCrawl = crawlUrl(bulkId, url, brainId, toast).catch((error) => {
                    uploadSuccess = false;
                    return Promise.reject(error);
                });
                tasks.push(urlCrawl);
            }

            await Promise.all(tasks);

            if (uploadSuccess) {
                toast.success("Upload Success", "File and/or URL processed successfully.");
                if (onUploadComplete) onUploadComplete();
            }
        } catch (error) {
            if (!uploadSuccess) {
                toast.error("Upload Failed", "An error occurred during upload or crawling.");
            }
        } finally {
            await chatService.deleteChat(bulkId);
            setLoadAnim(false);
            onClose();
        }
    };

    return (
        <Modal name="upload-knowledge" id={modal.id} onClose={onClose}>
            <div className="leave-container">
                <div
                    className="button-leave"
                    id="close_modal_container"
                    onClick={() => onClose({ idIconModal: "close_modal_container" })}>
                    <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
                </div>
            </div>
            <Form className="modal-form">
                <Form.Title>{title}</Form.Title>
                <p>{sub_title}</p>
                <Form.Body>
                    <div className="tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                onClick={() => setSelectedTab(tab)}
                                className={selectedTab === tab ? "active" : ""}>
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="tab-content">
                        {selectedTab === "Documents" && (
                            <FromDocuments setFile={setFile} file={file} loadAnim={loadAnim} />
                        )}
                        {selectedTab === "Connections" && <FromConnections />}
                        {selectedTab === "Websites" && <FromWebsites setUrl={setUrl} url={url} />}
                    </div>
                </Form.Body>
                <div className="flex">
                    <Button className={"cancel"} label="Cancel" onClick={onClose} style={{ margin: 10 }} />
                    <Button className={"validate"} label="Upload" onClick={onSubmit} style={{ margin: 10 }} />
                </div>
            </Form>
        </Modal>
    );
});

export default KnowledgeModal;
